import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  startOfMonth,
  endOfMonth,
  format,
  formatISO,
  parseISO,
  isValid,
  differenceInDays,
} from "date-fns";
import {
  GSC_DEFAULT_DATE_RANGE,
  isGscDateRangeOption,
  type GscDateRangeOption,
  type GscResolvedDateRange,
} from "@/types/gsc";

export interface GscComparePeriodRanges {
  current: { start: string; end: string };
  previous: { start: string; end: string };
}

export const formatDateOnly = (date: Date): string => format(date, "yyyy-MM-dd");

export const toDateOnly = (isoString: string): string => {
  const parsed = parseISO(isoString);
  return isValid(parsed) ? formatDateOnly(parsed) : isoString.slice(0, 10);
};

export const getDateRangeForPreset = (
  preset: string,
  baseDate: Date = new Date(),
): { from: string; to: string } => {
  const now = baseDate;
  let from: Date;
  let to: Date;

  switch (preset) {
    case "today":
      from = startOfDay(now);
      to = endOfDay(now);
      break;
    case "yesterday": {
      const yesterday = subDays(now, 1);
      from = startOfDay(yesterday);
      to = endOfDay(yesterday);
      break;
    }
    case "last_7_days":
      from = startOfDay(subDays(now, 6));
      to = endOfDay(now);
      break;
    case "last_14_days":
      from = startOfDay(subDays(now, 13));
      to = endOfDay(now);
      break;
    case "last_28_days":
      from = startOfDay(subDays(now, 27));
      to = endOfDay(now);
      break;
    case "last_90_days":
      from = startOfDay(subDays(now, 89));
      to = endOfDay(now);
      break;
    case "this_month":
      from = startOfMonth(now);
      to = endOfDay(now);
      break;
    case "last_month": {
      const lastMonth = subMonths(now, 1);
      from = startOfMonth(lastMonth);
      to = endOfMonth(lastMonth);
      break;
    }
    case "all_time":
      from = parseISO("2020-01-01");
      to = endOfDay(now);
      break;
    default:
      from = startOfMonth(now);
      to = endOfDay(now);
  }

  return { from: formatISO(from), to: formatISO(to) };
};

export const resolveGscDateRange = (
  dateRange: GscDateRangeOption,
  customFrom?: string,
  customTo?: string,
  baseDate: Date = new Date(),
): GscResolvedDateRange => {
  const toResolvedRange = (preset: string): GscResolvedDateRange => {
    const { from, to } = getDateRangeForPreset(preset, baseDate);
    return { dateFrom: toDateOnly(from), dateTo: toDateOnly(to) };
  };

  if (dateRange === "custom") {
    const fromDate = parseIsoDateParam(customFrom ?? null);
    const toDate = parseIsoDateParam(customTo ?? null);

    if (fromDate && toDate) {
      const [start, end] =
        fromDate <= toDate ? [fromDate, toDate] : [toDate, fromDate];
      return {
        dateFrom: formatDateOnly(startOfDay(start)),
        dateTo: formatDateOnly(startOfDay(end)),
      };
    }

    return toResolvedRange(GSC_DEFAULT_DATE_RANGE);
  }

  return toResolvedRange(dateRange);
};

export const parseGscDateRangeFromUrl = (
  searchParams: URLSearchParams,
): {
  dateRange: GscDateRangeOption;
  customFrom?: string;
  customTo?: string;
} => {
  const rawDateRange =
    searchParams.get("dateRange") ?? GSC_DEFAULT_DATE_RANGE;
  const dateRange = isGscDateRangeOption(rawDateRange)
    ? rawDateRange
    : GSC_DEFAULT_DATE_RANGE;

  const customFrom = searchParams.get("from") || undefined;
  const customTo = searchParams.get("to") || undefined;

  if (dateRange === "custom") {
    const fromDate = parseIsoDateParam(customFrom ?? null);
    const toDate = parseIsoDateParam(customTo ?? null);

    if (!fromDate || !toDate) {
      return { dateRange: GSC_DEFAULT_DATE_RANGE };
    }

    return { dateRange, customFrom, customTo };
  }

  return { dateRange };
};

/**
 * Aligns URL params with parsed GSC date range (invalid custom → default preset, strip orphan from/to).
 * Returns a new URLSearchParams when changes are needed, otherwise null.
 */
export const syncGscDateRangeSearchParams = (
  searchParams: URLSearchParams,
): URLSearchParams | null => {
  const next = new URLSearchParams(searchParams);
  let changed = false;

  const rawDateRange = searchParams.get("dateRange");

  if (rawDateRange && !isGscDateRangeOption(rawDateRange)) {
    next.set("dateRange", GSC_DEFAULT_DATE_RANGE);
    next.delete("from");
    next.delete("to");
    return next;
  }

  if (rawDateRange === "custom") {
    const fromDate = parseIsoDateParam(searchParams.get("from"));
    const toDate = parseIsoDateParam(searchParams.get("to"));

    if (!fromDate || !toDate) {
      next.set("dateRange", GSC_DEFAULT_DATE_RANGE);
      next.delete("from");
      next.delete("to");
      return next;
    }
  }

  const parsed = parseGscDateRangeFromUrl(searchParams);

  if (parsed.dateRange !== "custom") {
    if (next.has("from")) {
      next.delete("from");
      changed = true;
    }
    if (next.has("to")) {
      next.delete("to");
      changed = true;
    }
  }

  return changed ? next : null;
};

/** Previous period — mirrors PostgreSQL: period_days = to - from; prev_to = from - 1; prev_from = prev_to - period_days. */
export const getGscPreviousPeriod = (
  fromDate: Date,
  toDate: Date,
): { prevFrom: Date; prevTo: Date } => {
  const from = startOfDay(fromDate);
  const to = startOfDay(toDate);
  const periodDays = differenceInDays(to, from);
  const prevTo = subDays(from, 1);
  const prevFrom = subDays(prevTo, periodDays);
  return { prevFrom, prevTo };
};

/** Current vs previous period for GSC PoP labels (matches backend window logic). */
export const getGscComparePeriodRanges = (
  dateRange: GscDateRangeOption,
  customFrom?: string,
  customTo?: string,
  baseDate: Date = new Date(),
): GscComparePeriodRanges => {
  const { dateFrom, dateTo } = resolveGscDateRange(
    dateRange,
    customFrom,
    customTo,
    baseDate,
  );
  const currentStart = startOfDay(parseISO(dateFrom));
  const currentEnd = startOfDay(parseISO(dateTo));
  const { prevFrom, prevTo } = getGscPreviousPeriod(currentStart, currentEnd);

  return {
    current: {
      start: formatDateOnly(currentStart),
      end: formatDateOnly(currentEnd),
    },
    previous: {
      start: formatDateOnly(prevFrom),
      end: formatDateOnly(prevTo),
    },
  };
};

export const formatGscPeriodDate = (isoDate: string): string => {
  const parsed = parseISO(isoDate);
  return isValid(parsed) ? format(parsed, "MMM d") : isoDate;
};

export const formatGscPeriodComparisonLabel = (
  ranges: GscComparePeriodRanges,
): string => {
  const current = `${formatGscPeriodDate(ranges.current.start)} – ${formatGscPeriodDate(ranges.current.end)}`;
  const previous = `${formatGscPeriodDate(ranges.previous.start)} – ${formatGscPeriodDate(ranges.previous.end)}`;
  return `${current} vs ${previous}`;
};

export const formatGscUpdatedAtUtc = (timestamp: number): string => {
  const formatted = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hour12: false,
  }).format(new Date(timestamp));

  return `${formatted} UTC`;
};

/** Parses an ISO date string from URL/search params; returns undefined if missing or invalid. */
export const parseIsoDateParam = (param: string | null): Date | undefined => {
  if (!param?.trim()) return undefined;
  const parsed = parseISO(param.trim());
  return isValid(parsed) ? parsed : undefined;
};

export const getDefaultMonthDateRangeIso = (): { from: string; to: string } => {
  const now = new Date();
  return {
    from: formatISO(startOfMonth(now)),
    to: formatISO(endOfMonth(now)),
  };
};

export type AnalyticsDateRange = { startDate: string; endDate: string };

/**
 * get_analytics_dashboard takes `timestamp without time zone`, which silently
 * drops any offset: sending local wall clock shifts the whole window by the
 * browser's offset. Analytics data is stored in UTC, so send UTC instants.
 */
const toUtcIso = (value: string | Date): string =>
  (typeof value === "string" ? new Date(value) : value).toISOString();

export const parseAnalyticsDateRangeFromUrl = (url: URL): AnalyticsDateRange => {
  const fromDate = parseIsoDateParam(url.searchParams.get("from"));
  const toDate = parseIsoDateParam(url.searchParams.get("to"));

  if (fromDate && toDate) {
    const [start, end] =
      fromDate <= toDate ? [fromDate, toDate] : [toDate, fromDate];
    return { startDate: toUtcIso(start), endDate: toUtcIso(end) };
  }

  const preset = url.searchParams.get("preset");
  if (preset) {
    const { from, to } = getDateRangeForPreset(preset);
    return { startDate: toUtcIso(from), endDate: toUtcIso(to) };
  }

  const defaults = getDefaultMonthDateRangeIso();
  return { startDate: toUtcIso(defaults.from), endDate: toUtcIso(defaults.to) };
};

export const formatDate = (isoString: string) => {
  const d = parseISO(isoString);
  if (!isValid(d)) return isoString;
  return format(d, "dd MMMM yyyy HH:mm:ss");
};

